import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import LoginPage from "./components/LoginPage";
import DashboardLayout from "./components/Layout/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Rooms from "./pages/Rooms";
import Packages from "./pages/Packages";
import Staff from "./pages/Staff";
import Reports from "./pages/Reports";
import Calendar from "./pages/Calendar";
import AdminBooking from "./pages/AdminBooking";
import Bookings from "./pages/Bookings";
import Gallery from "./pages/Gallery";
import ExplorePage from "./pages/ExplorePage";
import Settings from "./pages/Settings";
import TicketPage from "./pages/TicketPage";
import HR from "./pages/HR";
import Inventory from "./pages/Inventory";
import AboutContent from "./pages/AboutContent";
import Coupons from "./pages/Coupons";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Blocks HotelStaff from accessing a route — redirects them to /bookings
const NoStaff = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'HotelStaff' ? <Navigate to="/bookings" replace /> : children;
};

// App Content Component
const AppContent = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  const homeRoute = user?.role === 'HotelStaff' ? '/bookings' : '/dashboard';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={homeRoute} replace /> : <LoginPage />}
      />
      <Route path="/" element={<Navigate to={isAuthenticated ? homeRoute : "/login"} replace />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="dashboard"
          element={user?.role === 'HotelStaff' ? <Navigate to="/bookings" replace /> : <Dashboard />}
        />
        <Route path="rooms" element={<Rooms />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="admin-booking" element={<AdminBooking />} />
        <Route path="tickets" element={<TicketPage />} />
        <Route path="hr" element={<HR />} />
        <Route path="inventory" element={<Inventory />} />
        {/* Staff cannot access below routes */}
        <Route path="reports"       element={<NoStaff><Reports /></NoStaff>} />
        <Route path="gallery"       element={<NoStaff><Gallery /></NoStaff>} />
        <Route path="staff"         element={<NoStaff><Staff /></NoStaff>} />
        <Route path="coupons"       element={<NoStaff><Coupons /></NoStaff>} />
        <Route path="packages"      element={<NoStaff><Packages /></NoStaff>} />
        <Route path="explore"       element={<NoStaff><ExplorePage /></NoStaff>} />
        <Route path="settings"      element={<NoStaff><Settings /></NoStaff>} />
        <Route path="about-content" element={<NoStaff><AboutContent /></NoStaff>} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;